import { OIDCUXMode, SSOUXMode, OAuthProvider } from "../clients/slashid-api"
import { createPopup } from "../utils/popup"
import { isValidHttpUrl } from "../utils/url"
import { Factor, OIDCWithExtras, SAMLWithExtras } from "./interfaces"
import { SlashIDEnvironment, isCustomEnvironment } from "../domain"
import { ERROR_NAMES } from "./errors"
import { SlashIDError } from "./errors.internal"

const ACCEPTED_BASE_URL_PROTOCOLS = ["http:", "https:"]

/**
 * This class simply holds connection options for your SlashID organization, to be passed to the
 * {@link SlashID} constructor.
 *
 * By default, the SDK will connect to the production environment and use the default SDK location:
 * ```js
 * const baseURL = "https://api.slashid.com"
 * const sdkURL = "https://cdn.slashid.com/sdk.html"
 *```
 *
 * You can configure the SlashID SDK to connect to the sandbox environment instead:
 * @example
 *
 * ```js
 * const sid = SlashID({
 *     environment: "sandbox"
 * })
 * ```
 *
 * Alternatively, you can specify a custom environment:
 * @example
 *
 * ```js
 * const sid = SlashID({
 *    environment: {
 *     baseURL: "https://api.custom.com",
 *     sdkURL: "https://sdk.custom/path.html"
 *   }
 * })
 * ```
 *
 * @remarks Specifying options for {@link SlashID} is optional and **only required in some cases**:
 *  - if your Organization has custom _WebAuthn_ scopes: use your SlashID-provided `sdkURL` value;
 *  - you are experimenting and want to connect to an environment other than production;
 *  - you want to enable analytics, in which case you must provide your Organization ID as the `oid` value
 */
export interface SlashIDOptions {
    /**
     * The base SlashID API endpoint.
     * @deprecated Use the `environment` option instead.
     */
    baseURL?: string

    /**
     * The location where your organization's custom SDK is served.
     * @deprecated Use the `environment` option instead.
     */
    sdkURL?: string

    /**
     * The environment to connect to - either "production" or "sandbox".
     * @default "production"
     *
     * To use a custom environment, pass an object with the following shape:
     * @example
     * environment: {
     *  baseURL: "https://{YOUR_CUSTOM_API_DOMAIN}}",
     *  sdkURL: "https://{YOUR_CUSTOM_SDK_DOMAIN}}/sdk.html"
     * }
     *
     */
    environment?: SlashIDEnvironment

    /**
     * Your Organization ID provided by SlashID
     */
    oid?: string

    /**
     * Whether to enable analytics or not.
     * @default true
     */
    analyticsEnabled?: boolean

    /**
     * Allow Identity Provider Initiated SSO.
     * The SDK expects the `iss` query parameter to be set with the following format: `social:PROVIDER_NAME:CLIENT_ID`.
     * @default false
     */
    identityProviderInitiatedSSOEnabled?: boolean
}

export function checkSlashIDOptions(options: any): asserts options is SlashIDOptions {
    if (!options) {
        throw new SlashIDError({
            message: "options cannot be null",
            name: ERROR_NAMES.invalidSlashIDConfigurationError,
            context: { options },
        })
    }

    if (options.environment) {
        if (
            typeof options.environment === "string" &&
            options.environment !== "production" &&
            options.environment !== "sandbox"
        ) {
            throw new SlashIDError({
                message: "If 'environment' is set as a string, it must be either 'production' or 'sandbox'",
                name: ERROR_NAMES.invalidSlashIDConfigurationError,
                context: { options },
            })
        }

        if (typeof options.environment === "object") {
            if (typeof options.environment.baseURL !== "string") {
                throw new SlashIDError({
                    message: "'environment.baseURL' must be a string",
                    name: ERROR_NAMES.invalidSlashIDConfigurationError,
                    context: { options },
                })
            }

            if (typeof options.environment.sdkURL !== "string") {
                throw new SlashIDError({
                    message: "'environment.sdkURL' must be a string",
                    name: ERROR_NAMES.invalidSlashIDConfigurationError,
                    context: { options },
                })
            }
        }
    }

    if (options.environment && (options.baseURL || options.sdkURL)) {
        throw new SlashIDError({
            message:
                "If 'environment' is set, 'baseURL' and 'sdkURL' cannot be set at the same time. Use a custom environment instead.",
            name: ERROR_NAMES.invalidSlashIDConfigurationError,
            context: { options },
        })
    }

    if (options.baseURL) {
        if (typeof options.baseURL !== "string") {
            throw new SlashIDError({
                message: "'baseURL' must be a string",
                name: ERROR_NAMES.invalidSlashIDConfigurationError,
                context: { options },
            })
        }

        let url: URL
        try {
            url = new URL(options.baseURL)
        } catch (_) {
            throw new SlashIDError({
                message: "'baseURL' must be a valid URL",
                name: ERROR_NAMES.invalidSlashIDConfigurationError,
                context: { options },
            })
        }

        if (ACCEPTED_BASE_URL_PROTOCOLS.indexOf(url.protocol) === -1) {
            throw new SlashIDError({
                message: "'baseURL' must be either an http or https URL",
                name: ERROR_NAMES.invalidSlashIDConfigurationError,
                context: { options },
            })
        }
    }

    if (options.sdkURL) {
        if (typeof options.sdkURL !== "string") {
            throw new SlashIDError({
                message: "'sdkURL' must be a string",
                name: ERROR_NAMES.invalidSlashIDConfigurationError,
                context: { options },
            })
        }

        try {
            new URL(options.sdkURL)
        } catch (_) {
            throw new SlashIDError({
                message: "'sdkURL' must be a valid URL",
                name: ERROR_NAMES.invalidSlashIDConfigurationError,
                context: { options },
            })
        }
    }

    const { analyticsEnabled = true, oid, identityProviderInitiatedSSOEnabled = false } = options

    if (analyticsEnabled && !oid) {
        throw new SlashIDError({
            message: "Analytics require the 'oid' parameter to be set when instantiating SlashID()",
            name: ERROR_NAMES.invalidSlashIDConfigurationError,
            context: { options },
        })
    }

    if (identityProviderInitiatedSSOEnabled && !oid) {
        throw new SlashIDError({
            message:
                "Identity provider initiated SSO requires the 'oid' parameter to be set when instantiating SlashID()",
            name: ERROR_NAMES.invalidSlashIDConfigurationError,
            context: { options },
        })
    }
}

function getSlashIDURLsFromOptions(options: SlashIDOptions): { baseURL: string; sdkURL: string } {
    // prefer the environment setting
    if (options.environment) {
        return getSlashIDURLsFromEnvironment(options.environment)
    }

    // default to production if individual options are not set
    return {
        baseURL: options.baseURL || SlashIDEnvironmentConfig.production.baseURL,
        sdkURL: options.sdkURL || SlashIDEnvironmentConfig.production.sdkURL,
    }
}

export const SlashIDEnvironmentConfig = {
    production: {
        baseURL: "https://api.slashid.com",
        sdkURL: "https://cdn.slashid.com/sdk.html",
    },
    sandbox: {
        baseURL: "https://api.sandbox.slashid.com",
        sdkURL: "https://cdn.sandbox.slashid.com/sdk.html",
    },
}

function getSlashIDURLsFromEnvironment(environment: SlashIDEnvironment): { baseURL: string; sdkURL: string } {
    if (typeof environment === "string") {
        return SlashIDEnvironmentConfig[environment]
    }

    if (isCustomEnvironment(environment)) {
        return {
            baseURL: environment.baseURL,
            sdkURL: environment.sdkURL,
        }
    }

    throw new TypeError("'environment' must be either a string or an object")
}

/**
 * Configuration options for the SlashID SDK.
 * Same as SlashIDOptions, but with all required options set.
 * Does not inherit the same set of fields as SlashIDOptions because some of them are going to be removed in the next major version.
 */
export type SlashIDConfig = {
    oid?: string
    baseURL: string
    sdkURL: string
    analyticsEnabled: boolean
    identityProviderInitiatedSSOEnabled: boolean
}

/**
 * Ensures all the required configuration options are set, applying default values to missing options.
 */
export function createSlashIDConfig(options: SlashIDOptions): SlashIDConfig {
    const urls = getSlashIDURLsFromOptions(options)

    const config: SlashIDConfig = {
        oid: options.oid,
        analyticsEnabled: options.analyticsEnabled ?? true,
        identityProviderInitiatedSSOEnabled: options.identityProviderInitiatedSSOEnabled ?? false,
        ...urls,
    }

    return config
}

export class OTPMethodOptions {
    public readonly getOTP?: () => Promise<string | undefined>

    constructor(obj?: any) {
        this.getOTP = obj?.getOTP
    }
}

export class TOTPMethodOptions {
    public readonly getOTP?: () => Promise<string | undefined>

    constructor(obj?: any) {
        this.getOTP = obj?.getOTP
    }
}

export async function createOIDCMethodOptions(authenticationFactor: Factor): Promise<OIDCWithExtras> {
    if (authenticationFactor.method !== "oidc") {
        throw new Error(`Can't create OIDC extras for method: ${authenticationFactor.method}`)
    }

    let extras = {}
    const { client_id, provider } = authenticationFactor.options!!
    let { ux_mode, redirect_target } = authenticationFactor.options!!

    if (!ux_mode) {
        ux_mode = OIDCUXMode.Popup
    }

    if (!redirect_target) {
        redirect_target = window.location.href
    }

    if (ux_mode === OIDCUXMode.Redirect) {
        if (!isValidHttpUrl(redirect_target)) {
            throw new Error(
                `If OIDC ux_mode was set to "redirect" a "redirect_target" is required, got ${redirect_target}`
            )
        }
    }

    if (ux_mode === OIDCUXMode.Popup) {
        const [popup, setPopupUrl] = createPopup()
        extras = { ...extras, popup, setPopupUrl }
    }

    return {
        ...authenticationFactor,
        options: {
            ...authenticationFactor.options,
            client_id,
            provider,
            ux_mode,
            redirect_target,
        },
        extras,
    }
}

export async function createSAMLMethodOptions(authenticationFactor: Factor): Promise<SAMLWithExtras> {
    if (authenticationFactor.method !== "saml") {
        throw new Error(`Can't create SAML extras for method: ${authenticationFactor.method}`)
    }

    let extras = {}
    let { ux_mode, redirect_target } = authenticationFactor.options!!

    if (!ux_mode) {
        ux_mode = SSOUXMode.Popup
    }

    if (!redirect_target) {
        redirect_target = window.location.href
    }

    if (ux_mode === SSOUXMode.Redirect) {
        if (!isValidHttpUrl(redirect_target)) {
            throw new Error(
                `If SAML ux_mode was set to "redirect" a "redirect_target" is required, got ${redirect_target}`
            )
        }
    }

    if (ux_mode === SSOUXMode.Popup) {
        const [popup, setPopupUrl] = createPopup()
        extras = { ...extras, popup, setPopupUrl }
    }

    return {
        ...authenticationFactor,
        options: {
            ...authenticationFactor.options!,
            ux_mode,
            redirect_target,
        },
        extras,
    }
}

export const IDP_INITIATED_SSO_QUERY_PARAM = "iss"

export type IdentityProviderInitiatedSSOParams = {
    /**
     * iss query param has the following format: "social:PROVIDER_NAME:CLIENT_ID"
     */
    iss: `social:${OAuthProvider}:${string}`
    // optional and not implemented for now
    loginHint?: string
}

function isValidIssuer(iss: string): iss is `social:${OAuthProvider}:${string}` {
    const parts = iss.split(":")
    return parts.length === 3 && parts[0] === "social"
}

export function getIdentityProviderInitiatedSSOParams(queryString: string): IdentityProviderInitiatedSSOParams | null {
    const urlParams = new URLSearchParams(queryString)
    const iss = urlParams.get(IDP_INITIATED_SSO_QUERY_PARAM)

    if (!iss || !isValidIssuer(iss)) {
        return null
    }

    return { iss }
}

export function removeIdentityProviderInitiatedSSOParams(location: Location): string {
    const url = new URL(location.href)
    url.searchParams.delete(IDP_INITIATED_SSO_QUERY_PARAM)
    return url.toString()
}

export async function createOIDCOptionsForIdPInitiatedSSO(
    params: IdentityProviderInitiatedSSOParams
): ReturnType<typeof createOIDCMethodOptions> {
    // social: prefix is unused at the moment
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, provider, client_id] = params.iss.split(":")

    return createOIDCMethodOptions({
        method: "oidc",
        options: {
            provider: provider as OAuthProvider,
            client_id,
            ux_mode: OIDCUXMode.Redirect,
            redirect_target: removeIdentityProviderInitiatedSSOParams(window.location),
        },
    })
}
